import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACIST_PATHS } from '../pharmacyPaths';
import { PHARMACY_COPY, statusLabel } from '../pharmacyCopy';
import {
  alertTypeLabel,
  alertTypePillClass,
  alertDetailText
} from '../components/alertsUi';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, EmptyBlock, formatNumber } from '../ui';

const copy = PHARMACY_COPY.DASHBOARD_SITE;

function QueueItem({ label, hint, value, to, tone = 'default', icon }) {
  const navigate = useNavigate();
  const hot = Number(value) > 0 && tone !== 'success';
  return (
    <button
      type="button"
      className={`cp-dash-queue__item${hot ? ' is-hot' : ''}${
        tone === 'danger' ? ' is-danger' : ''
      }`}
      onClick={() => navigate(to)}
    >
      <span className="cp-dash-queue__icon" aria-hidden>
        <Icon name={icon} size={16} />
      </span>
      <span className="cp-dash-queue__body">
        <span className="cp-dash-queue__label">{label}</span>
        {hint ? <span className="cp-dash-queue__hint">{hint}</span> : null}
      </span>
      <span className="cp-dash-queue__value">{formatNumber(value)}</span>
      <span className="cp-dash-queue__chevron" aria-hidden>
        <Icon name="chevron-right" size={14} />
      </span>
    </button>
  );
}

export default function PharmacistDashboardPage() {
  const { reloadNonce, workSiteId, workSites, toast } = usePharmacy();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stockRows, setStockRows] = useState([]);
  const [alertsData, setAlertsData] = useState(null);
  const [dispensations, setDispensations] = useState([]);
  const [pendingRequests, setPendingRequests] = useState(0);

  const workSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!workSiteId) {
        setStockRows([]);
        setAlertsData(null);
        setDispensations([]);
        setPendingRequests(0);
        return;
      }

      const settled = await Promise.allSettled([
        pharmacyService.getSiteStock(workSiteId, { site_inventory_only: 'true' }),
        pharmacyService.getAlerts({ site_id: workSiteId }),
        pharmacyService.listDispensations({ site_id: workSiteId, status: 'PENDING' }),
        pharmacyService.listSupplyRequests({ site_id: workSiteId, status: 'PENDING' })
      ]);

      const [stockSettled, alertsSettled, dispSettled, reqSettled] = settled;
      const unwrap = (s) => (s.status === 'fulfilled' ? s.value : null);

      const stockRes = unwrap(stockSettled);
      const alertsRes = unwrap(alertsSettled);
      const dispRes = unwrap(dispSettled);
      const reqRes = unwrap(reqSettled);

      setStockRows(stockRes?.success ? stockRes.data || [] : []);
      setAlertsData(alertsRes?.success ? alertsRes.data : null);
      setDispensations(dispRes?.success ? dispRes.data || [] : []);
      setPendingRequests(reqRes?.success ? (reqRes.data || []).length : 0);

      const failed = settled.filter((s) => s.status === 'rejected');
      if (failed.length === settled.length) {
        toast?.('danger', failed[0].reason?.message || 'Tableau de bord indisponible.');
      } else if (failed.length > 0) {
        console.warn(
          '[pharmacist-dashboard] partial load failure',
          failed.map((f) => f.reason?.message || f.reason)
        );
      }
    } catch (e) {
      toast?.('danger', e.message || 'Tableau de bord indisponible.');
    } finally {
      setLoading(false);
    }
  }, [workSiteId, toast]);

  useEffect(() => {
    load();
  }, [load, reloadNonce]);

  if (!workSiteId) {
    return <EmptyBlock label={copy.NO_SITE} />;
  }

  if (loading) return <LoadingBlock label="Chargement du tableau de bord…" />;

  const totalUnits = stockRows.reduce((acc, r) => acc + Number(r.on_hand || 0), 0);
  const lowStockCount = stockRows.filter((r) => r.stock_status === 'LOW_STOCK').length;
  const outStockCount = stockRows.filter((r) => r.stock_status === 'OUT_OF_STOCK').length;
  const expiring = Number(alertsData?.summary?.expiring || 0);
  const expired = Number(alertsData?.summary?.expired || 0);
  const recentAlerts = (alertsData?.alerts || []).slice(0, 8);
  const siteName = workSite?.name || `#${workSiteId}`;

  return (
    <div className="cp-dashboard cp-dashboard--v2">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.TITLE}
        description={copy.DESC.replace('{name}', siteName)}
        actions={
          <>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={16} />
            </button>
            <button
              type="button"
              className="cp-btn cp-btn--ghost cp-btn--sm"
              onClick={() => navigate(PHARMACIST_PATHS.REQUESTS)}
            >
              {copy.CTA_REQUESTS}
            </button>
            <button
              type="button"
              className="cp-btn cp-btn--primary cp-btn--sm"
              onClick={() => navigate(PHARMACIST_PATHS.DISPENSATION_NEW)}
            >
              <Icon name="plus" size={14} aria-hidden />
              {copy.CTA_DISPENSE}
            </button>
          </>
        }
      />

      <section className="cp-catalog-stats cp-catalog-stats--dash" aria-label="Indicateurs site">
        <article className="cp-catalog-stat cp-catalog-stat--ok">
          <span className="cp-catalog-stat__label">{copy.KPI_UNITS}</span>
          <span className="cp-catalog-stat__value">{formatNumber(totalUnits)}</span>
        </article>
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.KPI_MEDS}</span>
          <span className="cp-catalog-stat__value">{formatNumber(stockRows.length)}</span>
        </article>
        <article
          className={`cp-catalog-stat cp-catalog-stat--clickable${
            lowStockCount > 0 ? ' cp-catalog-stat--amber' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => navigate(PHARMACIST_PATHS.ALERTS)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(PHARMACIST_PATHS.ALERTS);
            }
          }}
        >
          <span className="cp-catalog-stat__label">{copy.KPI_LOW}</span>
          <span className="cp-catalog-stat__value">{formatNumber(lowStockCount)}</span>
        </article>
        <article
          className={`cp-catalog-stat cp-catalog-stat--clickable${
            outStockCount > 0 ? ' cp-catalog-stat--amber' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => navigate(PHARMACIST_PATHS.ALERTS)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(PHARMACIST_PATHS.ALERTS);
            }
          }}
        >
          <span className="cp-catalog-stat__label">{copy.KPI_OUT}</span>
          <span className="cp-catalog-stat__value">{formatNumber(outStockCount)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--amber">
          <span className="cp-catalog-stat__label">{copy.KPI_EXPIRING}</span>
          <span className="cp-catalog-stat__value">{formatNumber(expiring)}</span>
        </article>
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.KPI_EXPIRED}</span>
          <span className="cp-catalog-stat__value">{formatNumber(expired)}</span>
        </article>
      </section>

      <div className="cp-dash-grid">
        <section className="cp-card cp-dash-panel">
          <div className="cp-card__head">
            <div>
              <h2 className="cp-card__title">{copy.SECTION_QUEUE}</h2>
            </div>
          </div>
          <div className="cp-card__body cp-card__body--flush">
            <div className="cp-dash-queue">
              <QueueItem
                label={copy.QUEUE_DISPENSE}
                hint={copy.QUEUE_DISPENSE_HINT}
                value={dispensations.length}
                to={PHARMACIST_PATHS.DISPENSATIONS}
                tone={dispensations.length > 0 ? 'danger' : 'default'}
                icon="pills"
              />
              <QueueItem
                label={copy.QUEUE_REQUESTS}
                hint={copy.QUEUE_REQUESTS_HINT}
                value={pendingRequests}
                to={PHARMACIST_PATHS.REQUESTS}
                icon="list"
              />
              <QueueItem
                label={copy.QUEUE_STOCK}
                hint={copy.QUEUE_STOCK_HINT.replace('{n}', formatNumber(stockRows.length))}
                value={stockRows.length}
                to={PHARMACIST_PATHS.SITE_STOCKS}
                icon="warehouse"
              />
            </div>

            {dispensations.length > 0 ? (
              <ul className="cp-dash-alert-list cp-dash-alert-list--inset">
                {dispensations.slice(0, 5).map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      className="cp-dash-alert-list__item"
                      onClick={() => navigate(PHARMACIST_PATHS.DISPENSATION_DETAIL(d.id))}
                    >
                      <span className="cp-status-pill cp-status-pill--warn">
                        {statusLabel(d.status)}
                      </span>
                      <span className="cp-dash-alert-list__body">
                        <strong>{d.dispensation_number}</strong>
                        <span>{d.patient_name || 'Patient'}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="cp-dash-panel__empty">{copy.EMPTY_DISPENSE}</p>
            )}
          </div>
        </section>

        <section className="cp-card cp-dash-panel">
          <div className="cp-card__head">
            <div>
              <h2 className="cp-card__title">{copy.SECTION_ALERTS}</h2>
            </div>
            <button
              type="button"
              className="cp-btn cp-btn--ghost cp-btn--sm"
              onClick={() => navigate(PHARMACIST_PATHS.ALERTS)}
            >
              {copy.SEE_ALL}
            </button>
          </div>
          <div className="cp-card__body">
            {recentAlerts.length === 0 ? (
              <div className="cp-empty-state cp-empty-state--compact">
                <span className="cp-empty-state__icon" aria-hidden>
                  <Icon name="checkCircle" size={24} />
                </span>
                <h3 className="cp-empty-state__title">{copy.EMPTY_ALERTS}</h3>
              </div>
            ) : (
              <ul className="cp-dash-alert-list">
                {recentAlerts.map((a, idx) => (
                  <li key={`${a.type}-${a.medication_id}-${a.lot_id || idx}`}>
                    <button
                      type="button"
                      className="cp-dash-alert-list__item"
                      onClick={() => navigate(PHARMACIST_PATHS.ALERTS)}
                    >
                      <span className={`cp-status-pill ${alertTypePillClass(a.type)}`}>
                        {alertTypeLabel(a.type)}
                      </span>
                      <span className="cp-dash-alert-list__body">
                        <strong>{a.medication_name || a.message}</strong>
                        <span>{alertDetailText(a)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <section className="cp-dash-quick-section" aria-label={copy.SECTION_QUICK}>
        <h2 className="cp-dash-quick-section__title">{copy.SECTION_QUICK}</h2>
        <div className="cp-dash-quick">
          <Link to={PHARMACIST_PATHS.DISPENSATION_NEW} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="pills" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_DISPENSE}</span>
              <span className="cp-dash-quick__hint">
                {formatNumber(dispensations.length)} en attente
              </span>
            </span>
          </Link>
          <Link to={PHARMACIST_PATHS.REQUESTS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="list" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_REQUESTS}</span>
              <span className="cp-dash-quick__hint">{copy.QUEUE_REQUESTS_HINT}</span>
            </span>
          </Link>
          <Link to={PHARMACIST_PATHS.SITE_STOCKS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="warehouse" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_STOCK}</span>
              <span className="cp-dash-quick__hint">
                {formatNumber(stockRows.length)} références
              </span>
            </span>
          </Link>
          <Link to={PHARMACIST_PATHS.INVENTORIES} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="clipboard-list" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_INVENTORY}</span>
              <span className="cp-dash-quick__hint">{copy.QUICK_INVENTORY_HINT}</span>
            </span>
          </Link>
          <Link to={PHARMACIST_PATHS.ALERTS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="bell" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_ALERTS}</span>
              <span className="cp-dash-quick__hint">
                {formatNumber(outStockCount + lowStockCount + expiring + expired)} signalements
              </span>
            </span>
          </Link>
          <Link to={PHARMACIST_PATHS.MOVEMENTS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="history" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_MOVEMENTS}</span>
              <span className="cp-dash-quick__hint">Traçabilité du site</span>
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
